import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, Router } from '@angular/router';
import { CMS_CONFIG, ContentCacheModule, ContentCacheService, createApolloConfig } from '@cardihab/content-cache';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import * as Sentry from "@sentry/angular";
import { APOLLO_OPTIONS, ApolloModule } from 'apollo-angular';
import { ConflictPolicy, EvictionPolicy } from 'cardihab-fhir-cache/fhir-cache';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { InformationModalPage } from 'src/app/pages/authentication/site-code-entry/information-modal/information-modal';
import { SiteCodeEntryPage } from 'src/app/pages/authentication/site-code-entry/site-code-entry.page';
import { SiteModalPage } from 'src/app/pages/authentication/site-code-entry/site-modal/site-modal';
import { AuthService } from 'src/app/services/auth.service';
import { SwManagerService } from 'src/app/services/sw-manager.service';
import { Environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { UIComponentsModule } from './components/uicomponents.module';
import { ConsentPage } from './pages/authentication/consent/consent.page';
import { ContactUsPage } from './pages/authentication/contact-us/contact-us.page';
import { HomePage } from './pages/authentication/home/home.page';
import { LoginPage } from './pages/authentication/login/login.page';
import { RegisterPage } from './pages/authentication/register/register.page';
import { VerifyLoginPage } from './pages/authentication/verify-login/verify-login.page';
import { VerifySignUpPage } from './pages/authentication/verify-sign-up/verify-sign-up.page';
import { PipesModule } from './pipes/pipes.module';
import { StrapiAuthInterceptor } from './services/strapi-auth.service';
import { DirectivesModule } from 'src/app/directives/directives.module';

export function createCmsConfig(authService: AuthService) {
  return {
    strapiUrl: Environment.CONTENT_URL + '/graphql',
    tokenProvider: async () => {
      return (await authService.getToken()) || '';
    },
    publicResources: ['termsandcondition', 'contactus', 'registerpage'],
    cacheConfig: {
      storage: 'localStorage',
      keyPrefix: 'cms-cache-',
      retentionDays: 90,
      enableDebugMode: false,
    }
  };
}

export function apolloConfigFactory(cmsConfig: any) {
  return createApolloConfig(cmsConfig);
}


@NgModule({
  declarations: [AppComponent, HomePage, ConsentPage, LoginPage, RegisterPage, SiteCodeEntryPage, VerifySignUpPage, VerifyLoginPage, SiteModalPage, InformationModalPage, ContactUsPage],
  imports: [
    BrowserModule,
    CommonModule,
    IonicModule.forRoot({
      swipeBackEnabled: false,
    }),
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    NgxIntlTelInputModule,
    PipesModule,
    UIComponentsModule,
    ApolloModule,
    HttpClientModule,
    BrowserAnimationsModule,
    ContentCacheModule,
    DirectivesModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { },
      deps: [Sentry.TraceService],
      multi: true,
    },
    SwManagerService,
    HttpClientModule,
    ContentCacheService,
    {
      provide: 'cacheConfig',
      useValue: {
        encryptionKey: 'thisisanencryptionkey',
        resources: {
          Device: {
            pageSize: 3000,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          MedicationRequest: {
            pageSize: 3000,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          MedicationStatement: {
            pageSize: 3000,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          Goal: {
            pageSize: 3000,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          Provenance: {
            pageSize: 3000,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          CarePlan: {
            pageSize: 100,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          EpisodeOfCare: {
            pageSize: 100,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          Organization: {
            pageSize: 100,
            cacheLimit: 20000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
          Observation: {
            pageSize: 10000,
            cacheLimit: 200000,
            evictionPolicy: EvictionPolicy.FirstInFirstOut,
            conflictPolicy: ConflictPolicy.Overwrite,
            isLocalOnly: false,
          },
        },
        fhirDetails: {
          fhirJWT: null,
          fhirUrl: Environment.HAPI_FHIR_URL,
        }
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StrapiAuthInterceptor,
      multi: true
    },
    {
      provide: CMS_CONFIG,
      useFactory: createCmsConfig,
      deps: [AuthService] // Inject AuthService into factory
    },
    // Apollo Configuration
    {
      provide: APOLLO_OPTIONS,
      useFactory: apolloConfigFactory,
      deps: [CMS_CONFIG]
    },
    // Content Service
    ContentCacheService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
